import React, { useState, useEffect } from 'react';
import { Toast } from '../../../../components/common/Toast';
import SectionCard from '../../../../components/common/SectionCard';
import ToggleField from '../../../../components/common/ToggleField';
import { BlockServicesIcon } from '../../../../components/common/SvgIcon';
import GuidelineTab from './components/GuidelineTab';
import RulesTab from './components/RulesTab';
import AvailableServicesTab from './components/AvailableServicesTab';
import Skeleton from '../../../../components/common/Skeleton';

const save = `${LEGAL_PAGES.assets_url}/images/icons/save.png`;

const BlockServices = ({ isLoading, setIsLoading }) => {
  const [saving, setSaving]           = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('rules');
  const [enabled, setEnabled]         = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const res    = await fetch(`${LEGAL_PAGES.apiUrl}/cookie-block-services`, {
        headers: { 'X-WP-Nonce': LEGAL_PAGES.nonce },
      });
      const result = await res.json();
      if (result.success && result.data) {
        setEnabled(!!result.data.enable_service_blocking);
      }
    } catch (e) {
      Toast.error('Failed to load block services settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res    = await fetch(`${LEGAL_PAGES.apiUrl}/cookie-block-services`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
        body: JSON.stringify({ enable_service_blocking: enabled }),
      });
      const result = await res.json();
      if (result.success) {
        Toast.success('Block services settings saved!');
      } else {
        Toast.error('Failed to save settings.');
      }
    } catch (e) {
      Toast.error('An error occurred while saving.');
    } finally {
      setSaving(false);
    }
  };

  const subTabs = [
    { id: 'rules',              label: 'Rules' },
    { id: 'available_services', label: 'Available Services' },
    { id: 'guideline',          label: 'Guideline' },
  ];

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="space-y-6">

        {/* ── Enable toggle ── */}
        <SectionCard title="Service Blocking" icon={<BlockServicesIcon />}>
          {isLoading ? <Skeleton /> : (
            <div className="space-y-5">
              <ToggleField
                label="Enable Service Blocking"
                description="Block third-party services that install cookies"
                name="enable_service_blocking"
                checked={enabled}
                onChange={(name, value) => setEnabled(value)}
              />

              {/* Warning text — always visible */}
              <div className="text-sm text-[#6F6F7B] max-w-2xl space-y-2 pl-1">
                <p>
                  Read before activating: Service Blocking helps you to block third-party services
                  that install cookies, however, it may not be able to block all services and it
                  might remove other assets unintentionally. Make sure to test it locally or on a
                  staging site first before applying the settings to the production site.
                </p>
                <p>
                  You need to reload the page to make it work, or use the auto-reload feature in
                  General Settings → Auto-reload after consent set OR use the Google Tag Manager
                  for your tracking.
                </p>
              </div>
            </div>
          )}
        </SectionCard>

        {/* ── Blocked Services — only shown when enabled ── */}
        {enabled && (
          <SectionCard maxHeight="max-h-[1100px]" title="Blocked Services" icon={null}>

            {/* Sub-tab bar */}
            <div className="border-b border-[#E4E4E7] mb-6 -mt-2">
              <div className="flex">
                {subTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveSubTab(tab.id)}
                    className={[
                      'px-5 py-3 cursor-pointer text-sm font-medium border-b-2 transition -mb-px',
                      activeSubTab === tab.id
                        ? 'border-[#7C3BED] text-[#7C3BED]'
                        : 'border-transparent text-[#6F6F7B] hover:text-[#17171C]',
                    ].join(' ')}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sub-tab content */}
            {activeSubTab === 'rules'              && <RulesTab />}
            {activeSubTab === 'available_services' && <AvailableServicesTab />}
            {activeSubTab === 'guideline'          && <GuidelineTab />}

          </SectionCard>
        )}

        {/* ── Save button ── */}
        <div className="flex items-center justify-start gap-2 rounded-xl p-4">
          <button
            type="submit"
            disabled={saving}
            className={[
              'inline-flex items-center cursor-pointer justify-center rounded-xl px-6 py-[14px] text-sm font-semibold text-white shadow-[0px_4px_14px_-2px_#7C3BED40] transition',
              saving ? 'cursor-not-allowed bg-indigo-400' : 'bg-[#7C3BED] hover:bg-indigo-700',
            ].join(' ')}
          >
            <img src={save} alt="Save" className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

      </div>
    </form>
  );
};

export default BlockServices;